import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';

export type AIProductExampleData = {
  title: string[];
  description: string[];
  categories: string[][];
  images: string[];
  lastUsedExampleOf: {
    title: number;
    description: number;
    categories: number;
    images: number;
  };
};

export type AIProductExampleField = keyof Pick<AIProductExampleData, 'title' | 'description' | 'categories' | 'images'>;

type AIProductExampleStore = {
  getExampleOf: <K extends AIProductExampleField>(field: K) => AIProductExampleData[K][number] | undefined;
  setFields: (data: Partial<AIProductExampleData>) => void;
  hasData: () => boolean;
} & AIProductExampleData;

export const initialState: AIProductExampleData = {
  title: [],
  description: [],
  categories: [],
  images: [],
  lastUsedExampleOf: {
    title: 0,
    description: 0,
    categories: 0,
    images: 0
  }
};

export const aiProductExampleStore = create<AIProductExampleStore>()((set, _get) => ({
  ...initialState,

  getExampleOf: field => {
    const state = _get();
    const items = state[field];
    const lastIndex = state.lastUsedExampleOf[field];

    if (items.length === 0) {
      return;
    }

    const nextIndex = (lastIndex + 1) % items.length;

    set(state => ({
      lastUsedExampleOf: {
        ...state.lastUsedExampleOf,
        [field]: nextIndex
      }
    }));

    return items[nextIndex];
  },

  setFields: fields => {
    set(state => ({ ...state, ...fields }));
  },

  hasData: () => {
    const state = _get();
    return state.title.length > 0;
  }
}));

const useAIProductExampleStore = <T>(selector: (state: AIProductExampleStore) => T): T =>
  aiProductExampleStore(useShallow(selector));

export default useAIProductExampleStore;
